import React, { FunctionComponent, ReactNode, useContext, useEffect } from 'react'
import { TouchableOpacity } from 'react-native'

import { BlurView } from 'expo-blur'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { AccessibilityRolesNative, IS_WEB } from '../../constants'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { TabBarContext } from '../tabBar/TabBarContext'

export const FLOATING_TAB_BAR_HEIGHT = 64

export type FloatingTabBarTab = {
  key: string
  label: string
  icon: (isFocused: boolean, iconColor: string) => ReactNode
  onPress: () => void
  testID?: string
}

export type FloatingTabBarProps = {
  tabs: FloatingTabBarTab[]
  activeTabKey: string
  bottomInset?: number
  horizontalInset?: number
  useCenteredTabSpacing?: boolean
  testID?: string
}

const FloatingTabBarOuter = styled.View<{
  bottomInset: number
  horizontalInset: number
}>(({ bottomInset, horizontalInset }) => ({
  position: IS_WEB ? 'fixed' : 'absolute',
  bottom: bottomInset + 8,
  left: `${horizontalInset}px`,
  right: `${horizontalInset}px`,
  zIndex: IS_WEB ? 1 : undefined,
  borderRadius: 99,
  overflow: 'hidden',
}))

const FloatingTabItem = styled(TouchableOpacity)<{ isActive: boolean; indicatorColor: string; theme: ThemeType }>(
  ({ isActive, indicatorColor, theme }) => ({
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 44,
    paddingVertical: theme.spacing['12px'],
    borderTopWidth: isActive ? 2 : 0,
    borderTopColor: isActive ? indicatorColor : 'transparent',
  }),
)

const StyledBlurView = styled(BlurView)<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingHorizontal: theme.spacing['48px'],
  backgroundColor: theme.colors.components.floatingMobileNav.background,
}))

const TabItemContent = styled.View({
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
})

export const FloatingTabBar: FunctionComponent<FloatingTabBarProps> = ({
  tabs,
  activeTabKey,
  bottomInset = 0,
  horizontalInset = 8,
  useCenteredTabSpacing = false,
  testID = 'FloatingTabBar',
}) => {
  const { colors, isDarkMode } = useTheme() as ThemeType
  const tabBarContext = useContext(TabBarContext)

  useEffect(() => {
    if (!IS_WEB || !tabBarContext) {
      return
    }
    const height = bottomInset + 8 + FLOATING_TAB_BAR_HEIGHT
    tabBarContext.setTabBarHeight(height)
    tabBarContext.onTabBarVisibleChange(true)
    return () => {
      tabBarContext.onTabBarVisibleChange(false)
    }
  }, [bottomInset, tabBarContext])

  const BLUR_INTENSITY = IS_WEB ? 20 : 10 // Reduce intensity for native to improve scroll performance

  return (
    <FloatingTabBarOuter
      bottomInset={bottomInset}
      horizontalInset={horizontalInset}
      accessibilityRole={AccessibilityRolesNative.TAB_LIST}
      testID={tID(testID)}
    >
      <StyledBlurView
        tint={isDarkMode ? 'dark' : 'light'}
        intensity={BLUR_INTENSITY}
        style={useCenteredTabSpacing && tabs.length < 3 ? { justifyContent: 'center', gap: '62px' } : undefined}
      >
        {tabs.map((tab) => {
          const isFocused = tab.key === activeTabKey

          const iconColor = isFocused
            ? colors.components.floatingMobileNav.iconActive
            : colors.components.floatingMobileNav.iconDefault

          return (
            <FloatingTabItem
              key={tab.key}
              activeOpacity={1}
              isActive={isFocused}
              indicatorColor={colors.components.floatingMobileNav.activeIndicator}
              accessibilityRole={AccessibilityRolesNative.TAB}
              accessibilityState={isFocused ? { selected: true } : {}}
              accessibilityLabel={tab.label}
              onPress={tab.onPress}
              testID={tID(tab.testID ?? `${testID}-tab-${tab.key}`)}
            >
              <TabItemContent>
                {tab.icon(isFocused, iconColor)}
                <BodyText
                  size={BodyTextSize.CAPTION}
                  color={iconColor}
                  text={tab.label}
                  medium={isFocused}
                  regular={!isFocused}
                  numberOfLines={1}
                />
              </TabItemContent>
            </FloatingTabItem>
          )
        })}
      </StyledBlurView>
    </FloatingTabBarOuter>
  )
}
